import Modal from '../../components/Modal/Modal'
import { FormProvider, useForm } from 'react-hook-form'
import TextInput from '../../components/FormElements/TextInput'
import { Button } from '../../components/ui/Button'
import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import SelectInput from '../../components/FormElements/SelectInput'
import { useGetTransactionTypes } from '../../api/utils/useGetTransactionTypes'
import { useGetCurrencies } from '../../api/utils/useGetCurrencies'
import toast from 'react-hot-toast'
import GainStrategyOptions from '../../components/HelpScreens/GainStrategyOptions'
import { queryClient } from '../../main'
import { useEditUploadById } from '../../api/uploads/useEditUploadById'
import { useGetUploadById } from '../../api/uploads/useGetUploadById'

type FieldValues = {
  upload_name: string
  upload_description: string
  upload_type: { label: string; value: number }
  currency: { label: string; value: number }
  end_currency: { label: string; value: number }
  gain_strategy: { label: string; value: number }
}

const EditUpload = (): JSX.Element => {
  const { upload_id } = useParams()
  const { data } = useGetUploadById({ upload_id: Number(upload_id) })
  const upload_type = data?.transaction_type_id

  console.log(data)
  const { mutateAsync: editUpload } = useEditUploadById()

  const navigate = useNavigate()
  const formContext = useForm<FieldValues>({
    mode: 'onSubmit',
  })
  const {
    handleSubmit,
    formState: { isSubmitting },
    watch,
    reset,
    setValue,
  } = formContext

  const { data: transactionTypes } = useGetTransactionTypes()
  const { data: currencies } = useGetCurrencies()

  const transactionTypeOptions = useMemo(
    () =>
      transactionTypes?.map((d) => {
        return {
          label: d.name,
          value: d.id,
        }
      }),
    [transactionTypes],
  )

  const currencyOptions = useMemo(
    () =>
      currencies?.map((d) => {
        return {
          label: `${d.name} (${d.symbol})`,
          value: d.id,
        }
      }),
    [currencies],
  )

  const gainStrategyOptions = useMemo(
    () => [
      {
        label: 'Sale Date',
        value: 1,
      },
      {
        label: 'Sale date - Purchase date',
        value: 2,
      },
    ],
    [],
  )

  useEffect(() => {
    reset({
      upload_name: data?.name,
      upload_description: data?.description,
      currency: currencyOptions?.find((d) => d.value === data?.currency_id),
      gain_strategy: gainStrategyOptions?.find((d) => d.value === data?.gain_strategy),
      end_currency: currencyOptions?.find((d) => d.value === data?.end_currency_id),
    })
  }, [currencyOptions, data, gainStrategyOptions, reset, transactionTypeOptions])

  const onSubmit = async (values: FieldValues) => {
    const toastId = toast.loading('Making changes...')

    editUpload(
      {
        upload_id: Number(upload_id),
        upload_data: {
          name: values?.upload_name,
          description: values?.upload_description,
          currency_id: Number(values?.currency?.value),
          ...(Number(upload_type) === 2
            ? {
                gain_strategy: Number(values?.gain_strategy?.value),
              }
            : {}),
          ...(Number(upload_type) === 3
            ? {
                end_currency_id: Number(values?.end_currency?.value),
              }
            : {}),
        },
      },
      {
        onSuccess: (data) => {
          toast.success('File updated successfully!', { id: toastId })
          queryClient.invalidateQueries([
            'uploads',
            'list',
            { pageIndex: 1, pageSize: 10 },
          ])
          navigate('/dashboard/uploads')
        },
        onError: (error, variables, context) => {
          toast.error('An error occured', { id: toastId })
          navigate('/dashboard/uploads')
        },
      },
    )

    return
  }

  return (
    <Modal key={'EditUpload'} isOpen onClose={() => navigate('/dashboard/uploads')}>
      <FormProvider {...formContext}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col px-4 sm:px-8 py-6 w-full dark:bg-slate-800">
          <div className="pb-8 flex flex-col gap-3">
            <span className="text-foreground text-xl font-semibold">
              Edit {data?.name}
            </span>
            <span className="text-sm font-bold">
              Upload Type:{' '}
              {
                transactionTypeOptions?.find((d) => d.value === data?.transaction_type_id)
                  ?.label
              }
            </span>
          </div>
          <div className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <TextInput
                name="upload_name"
                label="Enter a name for your upload"
                type={'text'}
                required
                placeholder="Dividends - 2024"
              />
              <TextInput
                name="upload_description"
                label="Enter a description for your upload"
                type={'text'}
                placeholder="Dividends imported from Schwab"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <SelectInput
                required
                name={'currency'}
                label={Number(upload_type) === 3 ? 'Currency sent' : 'Currency'}
                options={currencyOptions ?? []}
                infoMessage={
                  Number(upload_type) === 3
                    ? ''
                    : 'Currency displayed on the transaction sheet'
                }
              />
              {Number(upload_type) === 2 && (
                <SelectInput
                  required
                  name={'gain_strategy'}
                  label={'Method for calculating gains'}
                  options={gainStrategyOptions}
                  helpMessage={<GainStrategyOptions />}
                  infoMessage={
                    "To switch from 'Sale Date' to 'Sale Date - Purchase Date' you must have mapped the required columns "
                  }
                />
              )}
              {Number(upload_type) === 3 && (
                <SelectInput
                  required
                  name={'end_currency'}
                  label={'Currency received'}
                  options={currencyOptions ?? []}
                />
              )}
            </div>
          </div>
          <div>
            <div
              className={
                'items-center flex justify-between border-t-2 py-4 mt-8 absolute bottom-0  left-0 w-full px-8 bg-white dark:bg-slate-700'
              }>
              <Button
                variant={'secondary'}
                disabled={isSubmitting}
                onClick={(e) => {
                  e.preventDefault()
                  navigate('/dashboard/uploads')
                }}>
                Discard
              </Button>
              <Button disabled={isSubmitting} type={'submit'}>
                Save Changes
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  )
}

export default EditUpload
